import { useSyncExternalStore } from "react";
import type { PageSnapshot } from "./types";

let snapshot: PageSnapshot | null = null;
const listeners = new Set<() => void>();

/** Mirror the stable, LLM-safe keys onto the existing assistant context channel
 *  (resolveContext.ts reads window.__wecoContext; chatApi sends it as body.context).
 *
 *  `facts` is deliberately NOT mirrored — it's client-matcher data.
 *
 *  Neither is `surface`, for a sharper reason: the two sides mean different
 *  things by that word. Here it is the page you are on ("file", "folder",
 *  "event"); on the assistant API it is where the assistant UI is drawn, a
 *  ChoiceField of exactly "assistant" | "drawer" | "mobile". Sending a page
 *  surface therefore 400s the entire chat request —
 *  `{"context":{"surface":["\"folder\" is not a valid choice."]}}` — which
 *  broke chat from any module publishing page context, not only Vault.
 *  Nothing on the server reads the field, so dropping it costs nothing: the
 *  agent still gets app/item_type/item_id, and the page surface stays here,
 *  where the playbooks match on it. */
/**
 * Apps the assistant API will accept. Mirrors `AppContextSerializer.app`
 * (services/assistant/core/api/serializers.py), a ChoiceField — an
 * out-of-vocabulary value 400s the ENTIRE chat request, not just the context.
 *
 * `PageSnapshot.app` is a bare string, so nothing stops a new publisher using
 * a name the server has never heard of. That is exactly how `surface` broke
 * chat from every module publishing page context; HomeWork is a shipped
 * module with no serializer choice, so the next publisher would do it again.
 * Dropping an unknown app costs the agent a hint; sending one costs the user
 * their assistant.
 */
const ASSISTANT_APPS = new Set([
  "email", "calendar", "drive", "docs", "grid", "present",
  "tasks", "video", "chat", "people", "assistant",
]);

function mirror(s: PageSnapshot | null) {
  const w = window as unknown as { __wecoContext?: unknown };
  if (!s) { delete w.__wecoContext; return; }
  const ctx: Record<string, unknown> = {};
  if (ASSISTANT_APPS.has(s.app)) ctx.app = s.app;
  if (s.item_type) ctx.item_type = s.item_type;
  if (s.item_id != null) ctx.item_id = s.item_id;
  w.__wecoContext = ctx;
}

export function setPageContext(s: PageSnapshot | null): void {
  snapshot = s;
  mirror(s);
  listeners.forEach((l) => l());
}

export function clearPageContext(): void {
  setPageContext(null);
}

export function usePageContext(): PageSnapshot | null {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => snapshot,
    () => snapshot,
  );
}

export function getPageContextForTest(): PageSnapshot | null { return snapshot; }
export function subscribeForTest(cb: () => void): () => void { listeners.add(cb); return () => listeners.delete(cb); }
